// Capa de API de mesas abiertas (backoffice, admin). Mismo molde que reservationsApi:
// thin wrappers sobre fetch con access-token contra /manage-mesas-abiertas.
// El backend responde { error, message, data }; error 1002 = sesión inválida → login.
// Errores de negocio del bloque 70xx: el motivo legible viene en message (AppError).

const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
  'access-token': localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

async function request(path, options = {}) {
  const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
  const body = await res.json();
  if (body && body.error && body.error > 0) {
    if (body.error === 1002) {
      const home = localStorage.getItem('homePageURL');
      if (home) window.location.href = home;
    }
    // Igual que reservationsApi: si el motivo puntual viene anidado (data.message) se
    // prefiere; el message de nivel superior es el genérico por código.
    const detail = body.data && body.data.message;
    const err = new Error(detail || body.message || 'Ocurrió un error');
    err.code = body.error;
    err.body = body;
    throw err;
  }
  return body ? body.data : null;
}

const qs = (params) => {
  const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&') : '';
};

// --- Publicación (FR-001…FR-008, FR-012) ---
export const listOpenTables = (status) => request(`manage-mesas-abiertas${qs({ status })}`);
export const getOpenTable = (id) => request(`manage-mesas-abiertas/${id}`);
export const createOpenTable = (payload) =>
  request('manage-mesas-abiertas', { method: 'POST', body: JSON.stringify(payload) });
export const updateOpenTable = (id, payload) =>
  request(`manage-mesas-abiertas/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
export const cancelOpenTable = (id) =>
  request(`manage-mesas-abiertas/${id}/cancel`, { method: 'POST' });

// --- Inscripciones (FR-009, FR-051, FR-055) ---
export const listSignups = (openTableId) => request(`manage-mesas-abiertas/${openTableId}/signups`);
export const checkInSignup = (signupId) =>
  request(`manage-mesas-abiertas/signups/${signupId}/check-in`, { method: 'POST' });
export const markNoShow = (signupId) =>
  request(`manage-mesas-abiertas/signups/${signupId}/no-show`, { method: 'POST' });
// Cobro presencial de una inscripción confirmada sin pago (enmienda For Test, FR-057/FR-058).
// paymentType = ids de ticket_enum del salón: 1 efectivo, 2 transferencia, 3 MercadoPago, 5 POS.
export const chargeSignup = (signupId, paymentType) =>
  request(`manage-mesas-abiertas/signups/${signupId}/charge`, {
    method: 'POST',
    body: JSON.stringify({ paymentType }),
  });
// Lugar sin cargo de socio aplicado desde el mostrador a una inscripción sin pagar: uno de sus
// lugares pasa a sin cargo (el backend valida la membresía y la jornada).
export const applySignupMemberSeat = (signupId, cedula) =>
  request(`manage-mesas-abiertas/signups/${signupId}/member-seat`, {
    method: 'POST',
    body: JSON.stringify({ cedula }),
  });

// --- Constantes UI (FR-011: etiquetas legibles, nunca el enum crudo) ---
export const OPEN_TABLE_STATUS = {
  published: { label: 'Publicada', badge: 'badge-success' },
  cancelled: { label: 'Cancelada', badge: 'badge-danger' },
  done:      { label: 'Realizada', badge: 'badge-secondary' },
};

export const SIGNUP_STATUS = {
  pending:        { label: 'Pendiente de pago', badge: 'badge-warning' },
  confirmed:      { label: 'Confirmada',        badge: 'badge-success' },
  expired:        { label: 'Vencida',           badge: 'badge-light' },
  cancelled:      { label: 'Cancelada',         badge: 'badge-danger' },
  refund_pending: { label: 'Por devolver',      badge: 'badge-warning' },
  no_show:        { label: 'No vino',           badge: 'badge-secondary' },
};

export const CHARGE_MODE_LABEL = {
  online: 'Pago online',
  libre:  'Libre (se cobra al llegar)',
};

// Chips de una inscripción, para las pantallas que la listan fuera de "Inscripciones"
// (la cuenta del salón y el detalle de la reserva). Son DOS preguntas distintas y por eso
// dos helpers: "¿está paga?" y "¿pasó algo más que el pago?" (hoy sólo "No vino": es el
// único estado que esas dos pantallas reciben además de la confirmada y del hold vivo —
// una cancelada, vencida o "por devolver" NO viaja en `openTableSignups`, no listarlas es
// deliberado y consistente con la cuenta).
// Los textos salen de acá y de SIGNUP_STATUS: nunca el enum crudo en pantalla (FR-011).

// El pago tiene TRES situaciones, no dos (FR-066): un hold de pago online en curso todavía
// no tiene pago sellado (`paid` false) pero no es el mismo caso que el que paga al llegar.
// Con "Pendiente" para los dos, el encargado le daba ese lugar a un walk-in creyendo que
// estaba libre, o lo mandaba a cobrar al mostrador y chargeSignup —que exige 'confirmed'—
// rebotaba con un 7003 que no explica nada. El texto es el de la pantalla de inscripciones
// ("Esperando pago online") y el color es otro: se distinguen sin leer.
export const signupPaymentChip = (signup) => {
  if (signup.paid) return { label: 'Paga', badge: 'badge-success' };
  if (signup.status === 'pending') return { label: 'Esperando pago online', badge: 'badge-info' };
  return { label: 'Pendiente', badge: 'badge-warning' };
};

// `confirmed` y `pending` no agregan nada al chip de pago (de una el pago dice si está paga
// o pendiente de cobro, de la otra dice que está pagando online): devuelve null y la fila
// muestra un solo chip.
export const signupStatusChip = (signup) => (
  signup.status === 'confirmed' || signup.status === 'pending'
    ? null
    : (SIGNUP_STATUS[signup.status] || { label: signup.status, badge: 'badge-light' })
);
